<template>
  <div class="panel place-detail-map">
    <div class="panel-content">
      <map-control
        ref="map-control"
        v-bind="$store.getters.mapControlBinds"
        v-bind:id-prefix="mapIdPrefix"
        v-bind:center="center"
        v-bind:zoom="zoomOnFitBounds"
        v-bind:store-sync="['mapType']"
        v-bind:control="{
          header: false,
          layerMenu: false,
        }"
        v-bind:can-change-indoor-level="false"
        v-bind:layer-renderers="layerRenderers"
        v-bind:reactive-render="reactiveRender && !isModalOverlaid"
        v-on:map-control-initend="$_onMapInitEnd"
        v-on:deck-overlay-initend="$_onDeckInitEnd"
      />
    </div>
    <map-layer-controller
      v-bind:layer-render-parameters="layerRenderParameters"
      v-on:map-layer-renderers-created="$_onMapLayerRenderersCreated"
    />
    <modal-overlay-detector
      v-on:is-modal-overlaid-changed="isModalOverlaid = $event"
    />
  </div>
</template>

<script>
import MapLayerController from '@/components/map/AppMapLayerController.vue'
import BvModalOverlayDetector from '@/components/AppBvModalOverlayDetector.vue'
import GeometryUtil from '@/js/util/GeometryUtil.js'
import { MapLayerMVTRenderer } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants.js'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'AppPanelPlaceDetailMap',
  components: {
    'map-layer-controller': MapLayerController,
    'modal-overlay-detector': BvModalOverlayDetector,
  },
  props: {
    /**
     * プレイスデータ
     */
    place: {
      type: Object,
      default: null,
    },
    /**
     * リアクティブに描画する場合はtrue
     *
     * ダイアログが非表示の間は描画を止めるために使う
     */
    reactiveRender: {
      type: Boolean,
      default: true,
    },
  },
  data: function () {
    return {
      /**
       * レイヤー描画パラメータ
       * @type {Array.Object}
       */
      layerRenderParameters: [],
      /**
       * レイヤーレンダラー
       * @type {Array.Object}
       */
      layerRenderers: [],
      /**
       * モーダルが重畳している場合はtrue
       * @type {Boolean}
       */
      isModalOverlaid: false,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `place-detail-${this._uid}-`
    },
    ...mapGetters('map', {
      /**
       * グローバルのレイヤー描画パラメータ
       * @type {Array.<Object>}
       */
      layerRenderParametersGlobal: 'layerRenderParameters',
      /**
       * 中心座標
       * @type {Object}
       */
      center: 'center',
    }),
    ...mapGetters('mapLayer', ['mapLayers']),
    ...mapGetters('mapIndoor', ['isGlobalMode']),
    /**
     * プレイスID
     * @type {Number}
     */
    placeId: function () {
      return (this.place && this.place.id) || null
    },
    /**
     * 表示範囲
     * @type {Object}
     */
    bounds: function () {
      if (!this.place) return null
      return GeometryUtil.getBounds(this.place.geom.geometry).toObject()
    },
    /**
     * 地図表示時fitBoundsのズーム
     * @type {Number}
     */
    zoomOnFitBounds: function () {
      return this.isGlobalMode ? 18 : 20
    },
  },
  watch: {
    bounds: function () {
      this.map && this.map.fitBounds(this.bounds, this.zoomOnFitBounds)
    },
    place: {
      immediate: true,
      handler: function () {
        this.$_setLayerRenderParameters()
      },
    },
  },
  mounted: function () {
    this.$_updateMapLayers()
  },
  methods: {
    /**
     * レイヤー描画パラメータを設定する
     */
    $_setLayerRenderParameters: function () {
      const params = _.cloneDeep(this.layerRenderParametersGlobal)
      for (const param of params) {
        if (param.id !== MapLayerMVTRenderer.LAYER_ID()) continue
        for (const subParam of param.subLayers) {
          const layer = this.mapLayers.find((l) => l.id === +subParam.id)
          if (!layer) continue
          if (layer.reference_table_type == FUNCTION_TYPE.PLACE) {
            // フィルタ設定
            const placeParam = { place_id: [this.placeId] }
            if (subParam.filter) {
              subParam.filter = { ...subParam.filter, ...placeParam }
            } else {
              subParam.filter = placeParam
            }
          } else {
            // 非表示化
            subParam.visible = false
          }
        }
      }
      this.layerRenderParameters = params
    },

    /**
     * レンダラー作成完了時処理
     * @param {Array.<Object>} renderers 作成されたレンダラー配列
     */
    $_onMapLayerRenderersCreated: function (renderers) {
      this.layerRenderers = renderers.map((r) => r.render)
    },

    /**
     * 地図初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
      this.bounds && this.map.fitBounds(this.bounds, this.zoomOnFitBounds)
    },

    /**
     * deckオーバーレイ初期化時処理
     */
    $_onDeckInitEnd: function () {
      this.deck = this.map && this.map.getMapDeckController()
    },

    /**
     * マップレイヤー更新
     */
    $_updateMapLayers: async function () {
      try {
        await this.$store.dispatch('mapLayer/updateMapLayersWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch maplayers. use fetched before.')
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.panel {
  width: 100%;
  height: 100%;
  color: $text-color;
  background-color: $dialog-background-color;
}

.place-detail-map {
  padding: 22px;
}

.panel-content {
  width: 100%;
  height: 100%;
  border: 1px solid #cccccc;
}
</style>
